import { Injectable } from '@angular/core';
import { useLocalization } from '@shared/localization';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, pairwise, tap, withLatestFrom } from 'rxjs/operators';

import { LabelNameProjector, Language, LocalizedName } from '@protocols/domain';

import { LabelEffects } from '../../../effects/label.effects';
import { PipelineCategoryEffects } from '../../../effects/pipeline-category.effects';
import {
  PipelineCategoryOption,
  PipelineCategoryState,
  PendingPersistence,
} from '../../../state/pipeline-category.state';
import { LabelData, LabelOverride } from '../../../state/label.state';

interface PendingEntity {
  uuid: string;
  name: LocalizedName;
  pendingPersistence: PendingPersistence;
}

@Injectable()
export class PipelineCategoryEditorFacade {
  private readonly localization = useLocalization();

  constructor(
    private readonly categoryEffects: PipelineCategoryEffects,
    private readonly categoryState: PipelineCategoryState,
    private readonly labelEffects: LabelEffects
  ) {
    this.wireLabelEffectsSync();
  }

  // ============================================================================
  // Selectors — State passthrough
  // ============================================================================

  public readonly methodType$ = this.categoryState.methodType$;
  public readonly variant$ = this.categoryState.variant$;
  public readonly readonly$ = this.categoryState.readonly$;
  public readonly supportsLabelColors$ = this.categoryState.supportsLabelColors$;
  public readonly category$ = this.categoryState.category$;
  public readonly categoryName$ = this.categoryState.categoryName$;
  public readonly categoryInput$ = this.categoryState.categoryInput$;
  public readonly options$ = this.categoryState.options$;
  public readonly optionInput$ = this.categoryState.optionInput$;
  public readonly saving$ = this.categoryState.saving$;
  public readonly loading$ = this.categoryState.loading$;
  public readonly error$ = this.categoryState.error$;
  public readonly status$ = this.categoryState.status$;
  public readonly result$ = this.categoryState.result$;

  // ============================================================================
  // Selectors — Localized display (active language with `en` fallback)
  // ============================================================================

  public readonly categoryDisplayName$: Observable<string> = combineLatest([
    this.categoryState.categoryName$,
    this.localization.currentLanguage$,
  ]).pipe(map(([name, lang]) => (name ? name[lang as Language] ?? name.en : '')));

  /**
   * `undefinedLabel` marks the rows the step editor's standing warning is about,
   * so the trail from the banner ends at the option that has to be fixed. It is
   * judged on the option's English name through the shared predicate, not on the
   * flattened display name beside it: the display name follows the active
   * language, so a placeholder that happens to have a Spanish translation would
   * otherwise read as properly defined, and a blank one would be
   * indistinguishable from a name that was never looked up.
   */
  public readonly optionsForDisplay$: Observable<
    { uuid: string; name: string; thrdis: number; color?: string; undefinedLabel: boolean }[]
  > = combineLatest([
    this.categoryState.options$,
    this.localization.currentLanguage$,
    this.categoryEffects.labelColorsByUuid$,
  ]).pipe(
    map(([options, lang, colorsByUuid]) =>
      options.map((option) => ({
        uuid: option.uuid,
        name: option.name[lang as Language] ?? option.name.en,
        thrdis: option.thrdis,
        color: colorsByUuid[option.uuid],
        undefinedLabel: !LabelNameProjector.isProperlyDefined(option),
      }))
    )
  );

  // ============================================================================
  // Selectors — Label suggestions passthrough
  // ============================================================================

  public readonly suggestions$ = this.labelEffects.labels$;
  public readonly suggestionsLoading$ = this.labelEffects.labelsLoaded$.pipe(map((loaded) => !loaded));
  public readonly hasMoreLabels$ = this.labelEffects.hasMoreItems$;
  public readonly loadingLabels$ = this.labelEffects.loading$;
  public readonly loadMoreLabels = this.labelEffects.loadMoreItems;

  // ============================================================================
  // Selectors — session context passthrough
  // ============================================================================

  /**
   * What the colour dialog needs from this editing session, as one snapshot: the
   * method types its own usage query cannot return (brand-new drafts and
   * renames) plus the method type being edited, if any. Derived on the Effects
   * tier; the opener reads it once when the colour action is clicked.
   */
  public readonly labelConfigDialogContext$ = this.categoryEffects.labelConfigDialogContext$;

  // ============================================================================
  // Derived selectors
  // ============================================================================

  public readonly canSubmit$: Observable<boolean> = this.categoryState.select(
    this.categoryState.category$,
    this.categoryState.saving$,
    this.categoryState.loading$,
    this.categoryState.variant$,
    this.categoryState.options$,
    (category, saving, loading, variant, options) =>
      !!category && !saving && !loading && (variant === 'basic' || options.length > 0)
  );

  public readonly showOptionsSection$: Observable<boolean> = this.categoryState.select(
    this.categoryState.variant$,
    this.categoryState.category$,
    (variant, category) => variant === 'options' && !!category
  );

  public readonly hasChanges$: Observable<boolean> = this.categoryState.select(
    this.categoryEffects.currentSignature$,
    this.categoryState.baselineSignature$,
    (current, baseline) => baseline !== null && current !== baseline
  );

  /**
   * Presentation-layer ephemera: tracks whether the category-name input has
   * been blurred at least once since the user last focused it. Lifetime
   * matches the facade (component-scoped via providers), so no manual
   * completion is needed.
   */
  private readonly categoryFieldBlurred$ = new BehaviorSubject<boolean>(false);

  public readonly categoryFieldError$: Observable<boolean> = combineLatest([
    this.categoryState.category$,
    this.categoryFieldBlurred$,
  ]).pipe(
    map(([category, blurred]) => blurred && !category),
    distinctUntilChanged()
  );

  // ============================================================================
  // Effects — attached to the most appropriate injected state
  // ============================================================================

  public readonly initializeAutocompletes = this.categoryState.effect<void>((trigger$) =>
    trigger$.pipe(map(() => this.labelEffects.initializeLabels()))
  );

  public readonly searchCategory = this.categoryState.effect((term$: Observable<string>) =>
    term$.pipe(
      map((term) => {
        this.categoryState.setCategoryInput(term);
        this.labelEffects.setSearchTerm(term);
        return term;
      })
    )
  );

  public readonly searchOption = this.categoryState.effect((term$: Observable<string>) =>
    term$.pipe(
      map((term) => {
        this.categoryState.setOptionInput(term);
        this.labelEffects.setSearchTerm(term);
        return term;
      })
    )
  );

  /*
   * Releases ownership of the shared label-search term. Called from blur
   * handlers so the input that just lost focus stops driving the suggestion
   * list, leaving the other input free to claim ownership on its next focus.
   */
  public readonly clearLabelSearch = this.categoryState.effect<void>((trigger$) =>
    trigger$.pipe(map(() => this.labelEffects.setSearchTerm('')))
  );

  public readonly pickCategory = this.categoryState.effect((label$: Observable<LabelData>) =>
    label$.pipe(
      map((label) => {
        this.categoryState.setCategoryFromLabel({
          uuid: label.uuid,
          name: this.toLocalizedName(label),
          pendingPersistence: undefined,
        });
        return label;
      })
    )
  );

  public readonly submitCategoryFreeText = this.categoryState.effect((name$: Observable<string>) =>
    name$.pipe(
      withLatestFrom(this.labelEffects.labels$),
      map(([name, labels]) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        const match = this.findLabelMatch(trimmed, labels);
        if (match) {
          this.categoryState.setCategoryFromLabel({
            uuid: match.uuid,
            name: this.toLocalizedName(match),
            pendingPersistence: undefined,
          });
        } else {
          this.categoryEffects.submitFreeTextCategory(trimmed);
        }
      })
    )
  );

  public readonly pickOption = this.categoryState.effect((label$: Observable<LabelData>) =>
    label$.pipe(
      map((label) => {
        this.categoryState.addOption({
          uuid: label.uuid,
          name: this.toLocalizedName(label),
          thrdis: 0,
          pendingPersistence: undefined,
        });
        this.resetOptionInput();
        return label;
      })
    )
  );

  public readonly submitOptionFreeText = this.categoryState.effect((name$: Observable<string>) =>
    name$.pipe(
      withLatestFrom(this.labelEffects.labels$),
      map(([name, labels]) => {
        const trimmed = name.trim();
        if (!trimmed) return;
        const match = this.findLabelMatch(trimmed, labels);
        if (match) {
          this.categoryState.addOption({
            uuid: match.uuid,
            name: this.toLocalizedName(match),
            thrdis: 0,
            pendingPersistence: undefined,
          });
        } else {
          this.categoryEffects.submitFreeTextOption(trimmed);
        }
        this.resetOptionInput();
      })
    )
  );

  public readonly removeOption = this.categoryState.removeOption;

  public readonly updateOptionThreshold = this.categoryState.effect(
    (input$: Observable<{ uuid: string; thrdis: number }>) =>
      input$.pipe(tap((input: { uuid: string; thrdis: number }) => this.categoryState.updateOptionThrdis(input)))
  );

  public readonly translateOption = this.categoryState.effect(
    (input$: Observable<{ uuid: string; values: Record<string, string> }>) =>
      input$.pipe(
        tap(({ uuid, values }) => {
          this.categoryState.updateOptionName({ uuid, name: values as LocalizedName });
        })
      )
  );

  public readonly translateCategory = this.categoryState.effect((values$: Observable<Record<string, string>>) =>
    values$.pipe(
      tap((values) => {
        this.categoryState.updateCategoryName(values as LocalizedName);
      })
    )
  );

  public readonly reorderOptions = this.categoryState.effect((orderedUuids$: Observable<string[]>) =>
    orderedUuids$.pipe(
      withLatestFrom(this.categoryState.options$),
      tap(([orderedUuids, options]) => {
        const byUuid = new Map(options.map((o) => [o.uuid, o]));
        const reordered = orderedUuids.map((uuid) => byUuid.get(uuid)).filter((o): o is PipelineCategoryOption => !!o);
        this.categoryState.replaceOptions(reordered);
      })
    )
  );

  public readonly save = this.categoryState.effect<void>((trigger$) =>
    trigger$.pipe(map(() => this.categoryEffects.save()))
  );

  public readonly cancel = this.categoryState.effect<void>((trigger$) =>
    trigger$.pipe(map(() => this.categoryEffects.cancel()))
  );

  public readonly markCategoryFieldBlurred = this.categoryState.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => {
        // Only honor blur if the field has actually been focused first.
        // Guards against framework-initiated blur events (e.g. MatDialog
        // focus trap rebalancing on open) firing the error without any
        // user interaction.
        if (!this.categoryFieldEverFocused) return;
        this.categoryFieldBlurred$.next(true);
      })
    )
  );

  public readonly markCategoryFieldFocused = this.categoryState.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.categoryFieldEverFocused = true;
        this.categoryFieldBlurred$.next(false);
      })
    )
  );

  /**
   * The facade may be registered at the parent's provider scope so the same
   * instance survives editor open/close cycles. Each fresh edit session must
   * start with a clean interaction state, this effect allows that.
   */
  public readonly resetFieldInteractionState = this.categoryState.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.categoryFieldEverFocused = false;
        this.categoryFieldBlurred$.next(false);
      })
    )
  );

  private categoryFieldEverFocused = false;

  private toLocalizedName(label: LabelData): LocalizedName {
    const dict = label.languages as Partial<Record<Language, string>>;
    if (!dict.en) {
      const fallback = Object.values(label.languages).find((v): v is string => !!v) ?? '';
      return { ...dict, en: fallback };
    }
    return dict as LocalizedName;
  }

  private findLabelMatch(term: string, labels: LabelData[]): LabelData | undefined {
    const normalized = term.trim().toLowerCase();
    if (!normalized) return undefined;
    return labels.find((label) =>
      Object.values(label.languages).some((v) => typeof v === 'string' && v.trim().toLowerCase() === normalized)
    );
  }

  private resetOptionInput(): void {
    this.categoryState.setOptionInput('');
    this.labelEffects.setSearchTerm('');
  }

  /**
   * Subscribes once to the editor's category/options buffer (tagged with the
   * session token) and diffs each pairwise emission to keep LabelEffects'
   * pendingLabels / labelOverrides slices in lockstep with the editor's
   * pendingPersistence ledger. Cancel's existing replaceOptions sweep (and the
   * matching category clear) flows through here automatically.
   *
   * The facade is provider-scoped above the dialog, so this subscription
   * outlives a single edit session. A token change marks a buffer swap
   * (`initialize` / `reset`): entities that vanish across it were not removed
   * by the user, so removals/transitions are suppressed and only additions
   * apply — otherwise re-opening the editor for a second category would
   * unstage the first category's still-referenced pending labels.
   */
  private wireLabelEffectsSync(): void {
    // ComponentStore observables complete on the store's destroy$, so plain
    // subscribe() here does not leak.
    this.categoryState
      // Project options, category and token from a single state snapshot so the
      // three always agree. Combining sibling selectors would glitch — the
      // token would lag the buffer on the very `initialize` emission this
      // guard exists to catch.
      .select((state) => ({
        options: state.options,
        category: state.category,
        sessionToken: state.sessionToken,
      }))
      .pipe(pairwise(), withLatestFrom(this.labelEffects.labelOverrides$))
      .subscribe(([[prev, curr], overrides]) => {
        const crossedSession = prev.sessionToken !== curr.sessionToken;
        this.applyOptionsDiff(prev.options, curr.options, overrides, crossedSession);
        this.applyCategoryDiff(prev.category, curr.category, overrides, crossedSession);
      });
  }

  private applyOptionsDiff(
    prev: PipelineCategoryOption[],
    curr: PipelineCategoryOption[],
    overrides: Record<string, LabelOverride>,
    crossedSession: boolean
  ): void {
    const prevByUuid = new Map(prev.map((o) => [o.uuid, o]));
    const currByUuid = new Map(curr.map((o) => [o.uuid, o]));

    if (!crossedSession) {
      for (const [uuid, prevOpt] of prevByUuid) {
        if (currByUuid.has(uuid)) continue;
        this.unregisterEntity(prevOpt);
      }
    }

    for (const [uuid, currOpt] of currByUuid) {
      const prevOpt = prevByUuid.get(uuid);
      if (!prevOpt) {
        this.registerAddition(currOpt, overrides);
      } else if (
        !crossedSession &&
        (prevOpt.pendingPersistence !== currOpt.pendingPersistence || !this.namesEqual(prevOpt.name, currOpt.name))
      ) {
        this.applyTransition(prevOpt, currOpt, overrides);
      }
    }
  }

  private applyCategoryDiff(
    prev: PendingEntity | null,
    curr: PendingEntity | null,
    overrides: Record<string, LabelOverride>,
    crossedSession: boolean
  ): void {
    if (!prev && !curr) return;
    if (!prev && curr) {
      this.registerAddition(curr, overrides);
      return;
    }
    if (prev && !curr) {
      if (crossedSession) return;
      this.unregisterEntity(prev);
      return;
    }
    // Both present
    if (prev.uuid !== curr.uuid) {
      if (!crossedSession) this.unregisterEntity(prev);
      this.registerAddition(curr, overrides);
      return;
    }
    if (
      !crossedSession &&
      (prev.pendingPersistence !== curr.pendingPersistence || !this.namesEqual(prev.name, curr.name))
    ) {
      this.applyTransition(prev, curr, overrides);
    }
  }

  private unregisterEntity(entity: PendingEntity): void {
    if (entity.pendingPersistence === 'save') {
      this.labelEffects.removePendingLabel(entity.uuid);
    } else if (entity.pendingPersistence === 'update') {
      this.labelEffects.removeLabelOverride(entity.uuid);
    }
  }

  private registerAddition(entity: PendingEntity, overrides: Record<string, LabelOverride>): void {
    if (entity.pendingPersistence === 'save') {
      this.labelEffects.addPendingLabel(this.entityToLabelData(entity));
    } else if (entity.pendingPersistence === 'update') {
      const existing = overrides[entity.uuid];
      const original = existing?.original ?? this.entityToLabelData(entity);
      this.labelEffects.setLabelOverride({ original, override: this.entityToLabelData(entity) });
    }
  }

  private applyTransition(prev: PendingEntity, curr: PendingEntity, overrides: Record<string, LabelOverride>): void {
    const prevP = prev.pendingPersistence;
    const currP = curr.pendingPersistence;

    if (currP === undefined) {
      if (prevP === 'save') this.labelEffects.removePendingLabel(curr.uuid);
      else if (prevP === 'update') this.labelEffects.removeLabelOverride(curr.uuid);
      return;
    }

    if (currP === 'save') {
      this.labelEffects.addPendingLabel(this.entityToLabelData(curr));
      if (prevP === 'update') this.labelEffects.removeLabelOverride(curr.uuid);
      return;
    }

    // currP === 'update'
    if (prevP === undefined) {
      this.labelEffects.setLabelOverride({
        original: this.entityToLabelData(prev),
        override: this.entityToLabelData(curr),
      });
    } else if (prevP === 'update') {
      const existing = overrides[curr.uuid];
      const original = existing?.original ?? this.entityToLabelData(prev);
      this.labelEffects.setLabelOverride({
        original,
        override: this.entityToLabelData(curr),
      });
    } else if (prevP === 'save') {
      this.labelEffects.removePendingLabel(curr.uuid);
      this.labelEffects.setLabelOverride({
        original: this.entityToLabelData(prev),
        override: this.entityToLabelData(curr),
      });
    }
  }

  private entityToLabelData(entity: PendingEntity): LabelData {
    // PendingEntity.name is LocalizedName (already dict-shaped); strip
    // undefined/null entries before handing it to LabelData.
    const languages = Object.fromEntries(
      Object.entries(entity.name).filter(([, v]) => v !== undefined && v !== null)
    ) as LabelData['languages'];
    return { id: entity.uuid, uuid: entity.uuid, languages };
  }

  private namesEqual(a: LocalizedName, b: LocalizedName): boolean {
    const aKeys = Object.keys(a) as Language[];
    const bKeys = Object.keys(b) as Language[];
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((k) => a[k] === b[k]);
  }
}
